// ./client/components/Reports/Graphs.js
import React from "react";
import { useAppContext } from "../../context/appContext";
import { calcOptionTotal } from "../../utils/calcOptionTotal";
import { numberToMoney } from "../../utils/money"
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

function BusinessReports() {
  ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

  const { reports, prices } = useAppContext();

  const options = {
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    stacked: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "FreeLite Metrics",
      },
    },
    scales: {
      y: {
        type: "linear",
        display: true,
        position: "left",
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  const maxTermforDays = 34;
  const minTermforMonths = 179; 
  const dayMilSecs = 1000 * 60 * 60 * 24;
  const cssMainLine = "#4d93ff";
  const cssMainBackground = "#005be6";
  const css2ndLine = "#00b063";
  const css2ndBackground = "#0f5132";

  const termLength = () => {
    const startDate = new Date(reports.startDate);
    const endDate = new Date(reports.endDate);

    const diffTime = endDate - startDate;

    return Math.ceil(diffTime / dayMilSecs);
  };

  const termDays = Array.from({ length: termLength() }, (_, i) => i + 1).map((day) => {
    console.log("startDate:", reports.startDate + "T00:00:00")
    const date = new Date(reports.startDate + "T00:00:00");
    date.setDate(date.getDate() + (day - 1));
    return date.toISOString();
  });

  const fillData = () => {
    let labels = [];
    let quantity = [];
    let value = [];
    let termQty = 0;
    let termVal = 0;
    let termNumber;

    const relAppts = reports.appointments?.filter(
      (appt) => appt.appt_date >= reports.startDate && appt.appt_date <= reports.endDate
    ).filter(appt => {
      const jobType = reports.jobs?.filter(job => job.id === appt.core_job_data_id)[0].job_type_id
      if(reports.includeService && jobType === 1) {
        return true
      }
      if(reports.includeInstall && jobType === 3) {
        return true
      }
      if(reports.includeCommercial && jobType === 4) {
        return true
      }
    });

    const calcValue = (crntJob, crntOption) => {
      // console.log("crntJob:", crntJob);
      // console.log("crntOption:", crntOption);

      const jobItems = reports.jobItems.filter((item) => item.options_id === crntOption.id);
      const fees = reports.fees.filter((fee) => fee.options_id === crntOption.id);
      const discounts = reports.discounts.filter((dsct) => dsct.options_id === crntOption.id);

      const totals = calcOptionTotal({
        jobItems,
        discounts,
        fees,
        sales_tax: crntOption.sales_tax,
        job: crntJob,
        prices,
      });

      // console.log("totals:", totals.grandTotal);
      return totals.grandTotal;
    };

    termDays.forEach((day, i) => {
      const date = new Date(day);
      const dayAppts = relAppts?.filter((appt) => {
        return appt.appt_date === day;
      });
      let dayQty = dayAppts?.length || 0;

      // Find job totals
      const dayVal = dayAppts?.reduce((ttl, currentAppt) => {
        const currentJob = reports.jobs?.filter(
          (job) => job.id === currentAppt.core_job_data_id
        )[0];
        const currentOptions = reports.options?.filter(
          (opt) => opt.core_job_data_id === currentAppt.core_job_data_id
        );

        // Determine value for estimates with multiple options
        if (currentOptions.length > 1) {
          // If chosen option not present then take an average value
          if (!currentJob.options_id) {
            let ttlVal = 0;
            let qty = 0; // keep track to remove custom price estimates
            currentOptions.forEach((opt) => {
              const val = calcValue(currentJob, opt);
              if (val) {
                qty = qty + 1;
                ttlVal = ttlVal + val;
              }
            });
            // If none of the estimates have a valid price
            if (qty === 0) {
              console.log(`Not counting this multi-option appointment (${currentAppt.id})! Daily quantity: `, dayQty)
              dayQty = dayQty - 1 // remove from day's quantity count
              return ttl // don't change the total value
            }

            return ttl + (ttlVal / qty);
          }
        }
        // Otherwise use single value for daily total
        const val = calcValue(currentJob, currentOptions[0]);
        // Don't count if custom price
        if (!val) {
          console.log(`Not counting this appointment (${currentAppt.id})! Daily quantity: `, dayQty)
          dayQty = dayQty - 1 // remove from day's quantity count
          return ttl // don't change the total value
        }

        return ttl + val;
      }, 0);

      // Compile monthly data
      if (termLength() > minTermforMonths) {
        termQty = termQty + dayQty;
        termVal = termVal + dayVal;
        // Start tracking for first month
        if (i === 0) {
          labels.push(date.toDateString().slice(4, -5));
          termNumber = date.getMonth();
        }
        // If it changes add and reset variables
        else if (date.getMonth() !== termNumber) {
          labels.push(date.toDateString().slice(4, -5));
          quantity.push(termQty);
          value.push(termVal);
          termQty = 0;
          termVal = 0;
          termNumber = date.getMonth();
        }
        // Add data from last month
        else if (i === termDays.length - 1) {
          quantity.push(termQty);
          value.push(termVal);
        }
      }

      // Or compile weekly data if desired
      else if (termLength() > maxTermforDays) {
        termQty = termQty + dayQty;
        termVal = termVal + dayVal;
        // Start tracking for first week
        if (i === 0) {
          labels.push(date.toDateString().slice(0, -5));
        }
        // If week starts over
        else if (i % 7 === 0) {
          labels.push(date.toDateString().slice(0, -5));
          quantity.push(termQty);
          value.push(termVal);
          termQty = 0;
          termVal = 0;
        }
        // Add data from last week
        else if (i === termDays.length - 1) {
          quantity.push(termQty);
          value.push(termVal);
        }
        
      }

      // Otherwise return daily data
      else {
        labels.push(date.toDateString().slice(0, -5));
        quantity.push(dayQty);
        // console.log("dayVal:", dayVal)
        value.push(dayVal);
      }
    });

    return { quantity, value, labels };
  };

  const data = fillData();
  const totalQty = data.quantity.reduce((a,c) => a + c, 0)
  const totalValue = data.value.reduce((a,c) => a + c, 0)
  const dataLength = data.labels?.length

  const myData = {
    labels: data?.labels,
    datasets: [
      {
        id: 1,
        label: `${reports?.datasetLabel} Quantity`,
        data: data?.quantity,
        fill: true,
        borderColor: cssMainLine,
        backgroundColor: cssMainBackground,
        yAxisID: "y",
        tension: 0.2,
      },
      {
        id: 2,
        label: `${reports?.datasetLabel} Total Value`,
        data: data?.value,
        borderColor: css2ndLine,
        backgroundColor: css2ndBackground,
        yAxisID: "y1",
        tension: 0.2,
      },
    ],
  };

  return (
    <div className="whole">
      {console.log("data:", data)}
      {!data.labels.length === 0 || !data.quantity.length === 0 || !data.value.length === 0 ? (
        <div>Loading...</div>
      ) : (
        <>
          {/* ----- Graphs ------ */}
          <Line key={data.value[0]} options={options} data={myData} />
          {/* ----- Totals ----- */}
          <div className="form-row report-totals">
          <br/>
          Total quantity: {totalQty}<br/>
          Average quantity per term: {(totalQty/dataLength).toFixed(1)}<br/>
          Total value: {numberToMoney(totalValue)}<br/>
          Average value per term: {numberToMoney(totalValue/dataLength)}<br/>
          </div>
        </>
      )}
    </div>
  );
}
export default BusinessReports;
